import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Empty } from "@/components/ui/notify/Empty";
import { Notify } from "@/components/ui/notify";
import { useProductionOrders } from "@/features/production-orders/hooks";
import type { ProductionOrder } from "@/features/production-orders/hooks";
import type { SandboxPeriod } from "@/types/sim-sandbox.types";

interface Props {
  lineId: string;
  period: SandboxPeriod;
  onChange: (patch: Partial<SandboxPeriod>) => void;
  onProduct?: (productId: string) => void;
}

const primaryProductId = (op: ProductionOrder): string | null =>
  op.products.find((p) => p.isPrimary)?.productId ?? op.products[0]?.productId ?? null;

const fmt = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

// Período por Ordem de Produção: seleciona a OP da linha (canceladas fora),
// prefill de quantidade/janela e, para OP concluída, alterna backtest (duração real vs planejada).
export function OpPeriodPicker({ lineId, period, onChange, onProduct }: Props) {
  const opsQuery = useProductionOrders({ lineId });
  const { data, isLoading, isError, error, refetch } = opsQuery;
  const orders = (data?.items ?? []).filter((o) => o.status !== "CANCELLED");
  const selected = orders.find((o) => o.id === period.sourceProductionOrderId) ?? null;
  const canBacktest = selected?.status === "COMPLETED";

  function pick(opId: string) {
    const op = orders.find((o) => o.id === opId);
    if (!op) {
      onChange({ sourceProductionOrderId: null });
      return;
    }
    onChange({
      mode: op.status === "COMPLETED" ? "OP_EXECUTED" : "OP_PLANNED",
      sourceProductionOrderId: op.id,
    });
    const pid = primaryProductId(op);
    if (pid && onProduct) onProduct(pid);
  }

  // Erro não pode virar "Sem Ordens de Produção" (indistinguível de "linha sem OPs").
  if (isError) return <Notify.QueryError error={error} onRetry={() => void refetch()} />;
  if (isLoading) return <p className="text-sm text-muted-foreground">Carregando ordens…</p>;
  if (orders.length === 0) {
    return (
      <Empty
        title="Sem Ordens de Produção nesta linha"
        description="Use o intervalo livre para simular um período qualquer."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor="sbx-op" className="text-xs">
          Ordem de Produção
        </Label>
        <Select
          id="sbx-op"
          value={period.sourceProductionOrderId ?? ""}
          onChange={(e) => pick(e.target.value)}
        >
          <option value="">Selecione uma OP…</option>
          {orders.map((o) => (
            <option key={o.id} value={o.id}>
              {(o.code ?? "OP") +
                " · " +
                fmt(o.plannedStartAt) +
                (o.status === "COMPLETED" ? " (concluída)" : "")}
            </option>
          ))}
        </Select>
      </div>

      {selected ? (
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 rounded-md bg-muted/50 p-3 text-xs">
          <dt className="text-muted-foreground">Quantidade planejada</dt>
          <dd className="text-right font-medium">{selected.plannedQty.toLocaleString("pt-BR")}</dd>
          <dt className="text-muted-foreground">Início</dt>
          <dd className="text-right">{fmt(selected.plannedStartAt)}</dd>
          <dt className="text-muted-foreground">Fim previsto</dt>
          <dd className="text-right">{fmt(selected.expectedEnd)}</dd>
        </dl>
      ) : null}

      {canBacktest ? (
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-input accent-primary"
            checked={period.mode === "OP_EXECUTED"}
            onChange={(e) => onChange({ mode: e.target.checked ? "OP_EXECUTED" : "OP_PLANNED" })}
          />
          Backtest: usar a duração real da execução (compara simulado × real)
        </label>
      ) : null}
    </div>
  );
}
